import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, History, UserPlus, ShieldCheck, UserMinus, Coins, Ban, CheckCircle2, Download, RefreshCw, Search, X, ChevronRight, FileText, Megaphone, Drama, LogIn, AlertTriangle } from "lucide-react";
import { StaffLogins, isOddHour } from "./StaffLogins";
import { AuditDetail, splitMeta } from "./AuditDetail";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { isCanceled, useLatestRequest } from "../../lib/useLatest";
import { ROLE_LABEL } from "./PlatformApp";
import { PageHeader } from "../../components/PageHeader";
import { Pager } from "../../components/Pager";

const PAGE_SIZE = 25;
const FETCH_CAP = 500;

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("id-ID", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "-");
const roleName = (r) => ROLE_LABEL[r] || r || "—";

// action code → { category, icon, tint, describe(target, meta) }
const ACTIONS = {
  "staff.grant": { cat: "staff", icon: UserPlus, tint: "text-emerald-600 bg-emerald-50", label: "Tambah staf",
    describe: (t, m) => `${m?.created ? "Mengundang" : "Memberi peran"} ${t} sebagai ${roleName(m?.role)}` },
  "staff.role": { cat: "staff", icon: ShieldCheck, tint: "text-blue-600 bg-blue-50", label: "Ubah peran",
    describe: (t, m) => `Mengubah peran ${t}: ${roleName(m?.from)} → ${roleName(m?.to)}` },
  "staff.revoke": { cat: "staff", icon: UserMinus, tint: "text-rose-600 bg-rose-50", label: "Cabut akses",
    describe: (t, m) => `Mencabut akses platform ${t} (${roleName(m?.role)})` },
  "user.credits": { cat: "credits", icon: Coins, tint: "text-amber-600 bg-amber-50", label: "Top up kredit",
    describe: (t, m) => `Top up +${new Intl.NumberFormat("id-ID").format(m?.delta ?? 0)} kredit untuk ${t} (saldo ${m?.balance_after ?? "—"})${m?.note ? ` · ${m.note}` : ""}` },
  "user.disable": { cat: "status", icon: Ban, tint: "text-rose-600 bg-rose-50", label: "Suspend akun",
    describe: (t, m) => `Men-suspend ${t}${m?.reason ? ` · ${m.reason}` : ""}` },
  "user.enable": { cat: "status", icon: CheckCircle2, tint: "text-emerald-600 bg-emerald-50", label: "Aktifkan akun",
    describe: (t, m) => `Mengaktifkan kembali ${t}${m?.reason ? ` · ${m.reason}` : ""}` },
  "finance.export": { cat: "export", icon: Download, tint: "text-violet-600 bg-violet-50", label: "Ekspor laporan",
    describe: (t, m) => `Ekspor laporan keuangan ${t}${m?.group ? ` (per ${m.group})` : ""}` },
  "audit.export": { cat: "export", icon: Download, tint: "text-violet-600 bg-violet-50", label: "Ekspor audit",
    describe: (t, m) => `Mengunduh jejak audit (${m?.count ?? 0} baris)${t ? ` · ${t}` : ""}` },
  "expense.create": { cat: "expense", icon: Coins, tint: "text-amber-600 bg-amber-50", label: "Pengeluaran",
    describe: (t, m) => `Mencatat pengeluaran ${t} · ${new Intl.NumberFormat("id-ID").format(m?.amount_idr ?? 0)} IDR${m?.taxable ? ` (PPN ${new Intl.NumberFormat("id-ID").format(m?.ppn_idr ?? 0)})` : ""}` },
  "expense.delete": { cat: "expense", icon: UserMinus, tint: "text-rose-600 bg-rose-50", label: "Hapus pengeluaran",
    describe: (t, m) => `Menghapus pengeluaran ${t}${m?.amount_idr ? ` · ${new Intl.NumberFormat("id-ID").format(m.amount_idr)} IDR` : ""}` },
  "expense.export": { cat: "export", icon: Download, tint: "text-violet-600 bg-violet-50", label: "Ekspor pengeluaran",
    describe: (t, m) => `Mengunduh data pengeluaran (${m?.count ?? 0} baris)${t ? ` · ${t}` : ""}` },
  "expense.efaktur": { cat: "expense", icon: Download, tint: "text-blue-600 bg-blue-50", label: "Unggah e-faktur",
    describe: (t) => `Mengunggah e-faktur untuk ${t}` },
  "recurring.create": { cat: "expense", icon: Coins, tint: "text-emerald-600 bg-emerald-50", label: "Langganan baru",
    describe: (t, m) => `Membuat langganan ${t} · ${new Intl.NumberFormat("id-ID").format(m?.amount_idr ?? 0)} IDR/bln` },
  "recurring.update": { cat: "expense", icon: ShieldCheck, tint: "text-blue-600 bg-blue-50", label: "Ubah langganan",
    describe: (t, m) => `Memperbarui langganan ${t}${m && "active" in m ? ` · ${m.active ? "aktif" : "nonaktif"}` : ""}` },
  "recurring.delete": { cat: "expense", icon: UserMinus, tint: "text-rose-600 bg-rose-50", label: "Hapus langganan",
    describe: (t) => `Menghapus langganan ${t}` },
  "recurring.run": { cat: "expense", icon: Coins, tint: "text-amber-600 bg-amber-50", label: "Jalankan langganan",
    describe: (t, m) => `Membuat ${m?.created ?? 0} pengeluaran langganan untuk ${t}` },
  "ppn.export": { cat: "export", icon: Download, tint: "text-violet-600 bg-violet-50", label: "Ekspor SPT PPN",
    describe: (t) => `Mengunduh SPT PPN tahun ${t}` },
  "pnl.export": { cat: "export", icon: Download, tint: "text-violet-600 bg-violet-50", label: "Ekspor laba rugi",
    describe: (t) => `Mengunduh laporan laba rugi ${t}` },
  "budget.set": { cat: "expense", icon: ShieldCheck, tint: "text-blue-600 bg-blue-50", label: "Atur anggaran",
    describe: (t, m) => `Menetapkan anggaran bulanan ${new Intl.NumberFormat("id-ID").format(m?.monthly_idr ?? 0)} IDR` },
  "pricing.update": { cat: "config", icon: ShieldCheck, tint: "text-indigo-600 bg-indigo-50", label: "Ubah tarif",
    describe: (t, m) => `Mengubah tarif & margin${summarizeChanged(m?.changed)}` },
  "trial.update": { cat: "config", icon: ShieldCheck, tint: "text-indigo-600 bg-indigo-50", label: "Ubah trial",
    describe: (t, m) => `Mengubah pengaturan trial${summarizeChanged(m?.changed)}` },
  "rate_limits.update": { cat: "config", icon: ShieldCheck, tint: "text-indigo-600 bg-indigo-50", label: "Ubah batas",
    describe: (t, m) => `Mengubah batas & rate limit${summarizeChanged(m?.changed)}` },
  "auth.login": { cat: "login", icon: LogIn, tint: "text-sky-600 bg-sky-50", label: "Login",
    describe: (t, m) => `Masuk${m?.ip ? ` dari ${m.ip}` : ""}${m?.user_agent ? ` · ${String(m.user_agent).slice(0, 40)}` : ""}` },
  "auth.login_failed": { cat: "login", icon: AlertTriangle, tint: "text-rose-600 bg-rose-50", label: "Login gagal",
    describe: (t, m) => `Gagal masuk${t ? ` sebagai ${t}` : ""}${m?.ip ? ` dari ${m.ip}` : ""}${m?.reason ? ` · ${m.reason}` : ""}` },
  "support_knowledge.add": { cat: "content", icon: FileText, tint: "text-emerald-600 bg-emerald-50", label: "Tambah pengetahuan", describe: (t, m) => `Menambah dokumen "${t}"${m?.chars ? ` · ${m.chars.toLocaleString("id-ID")} karakter` : ""}` },
  "support_knowledge.update": { cat: "content", icon: FileText, tint: "text-blue-600 bg-blue-50", label: "Ubah pengetahuan", describe: (t, m) => `Mengubah dokumen "${t}"${"enabled" in (m || {}) ? (m.enabled ? " · diaktifkan" : " · dinonaktifkan") : ""}` },
  "support_knowledge.delete": { cat: "content", icon: FileText, tint: "text-rose-600 bg-rose-50", label: "Hapus pengetahuan", describe: (t) => `Menghapus dokumen ${t}` },
  "announcement.create": { cat: "content", icon: Megaphone, tint: "text-emerald-600 bg-emerald-50", label: "Buat pengumuman", describe: (t, m) => `Membuat pengumuman "${t}"${m?.status ? ` · ${m.status}` : ""}` },
  "announcement.update": { cat: "content", icon: Megaphone, tint: "text-blue-600 bg-blue-50", label: "Ubah pengumuman", describe: (t) => `Mengubah pengumuman "${t}"` },
  "announcement.cancel": { cat: "content", icon: Megaphone, tint: "text-amber-600 bg-amber-50", label: "Batalkan pengumuman", describe: (t) => `Membatalkan pengumuman "${t}"` },
  "announcement.delete": { cat: "content", icon: Megaphone, tint: "text-rose-600 bg-rose-50", label: "Hapus pengumuman", describe: (t) => `Menghapus pengumuman "${t}"` },
  "maintenance.schedule": { cat: "content", icon: Megaphone, tint: "text-violet-600 bg-violet-50", label: "Jadwalkan pemeliharaan", describe: (t) => `Menjadwalkan "${t}"` },
  "persona_character.create": { cat: "content", icon: Drama, tint: "text-emerald-600 bg-emerald-50", label: "Buat karakter", describe: (t) => `Membuat karakter "${t}"` },
  "persona_character.update": { cat: "content", icon: Drama, tint: "text-blue-600 bg-blue-50", label: "Ubah karakter", describe: (t, m) => `Mengubah karakter "${t}"${"enabled" in (m || {}) ? (m.enabled ? " · aktif" : " · nonaktif") : ""}` },
};

// Turn the audit `changed` map ({field:[old,new]} or {field:"diubah"}) into a short human sentence.
function summarizeChanged(changed) {
  if (!changed || typeof changed !== "object") return "";
  const keys = Object.keys(changed);
  if (!keys.length) return "";
  const parts = keys.slice(0, 4).map((k) => {
    const v = changed[k];
    return Array.isArray(v) ? `${k}: ${v[0]} → ${v[1]}` : `${k}`;
  });
  const extra = keys.length > 4 ? ` +${keys.length - 4} lainnya` : "";
  return ` · ${parts.join(", ")}${extra}`;
}
const fallback = (code) => ({ cat: "other", icon: History, tint: "text-slate-600 bg-slate-100", label: code, describe: (t, m) => `${t || ""}${m && Object.keys(m).length ? ` · ${JSON.stringify(m)}` : ""}` });

const FILTERS = [
  { id: "all", label: "Semua" },
  { id: "staff", label: "Staf & Peran" },
  { id: "credits", label: "Kredit" },
  { id: "expense", label: "Pengeluaran" },
  { id: "config", label: "Tarif & Batas" },
  { id: "status", label: "Status akun" },
  { id: "export", label: "Ekspor" },
  { id: "content", label: "Konten" },
  { id: "login", label: "Login" },
];

export default function PlatformAudit() {
  const [items, setItems] = useState(null);
  const [page, setPage] = useState(0);
  const [staff, setStaff] = useState([]);
  const [actor, setActor] = useState("");
  const [sel, setSel] = useState(null);
  const closeSel = useCallback(() => setSel(null), []);
  const [filter, setFilter] = useState("all");
  const [refreshing, setRefreshing] = useState(false);
  const [q, setQ] = useState("");
  const [dq, setDq] = useState(""); // debounced actor query
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [downloading, setDownloading] = useState(false);

  useEffect(() => { const t = setTimeout(() => setDq(q.trim()), 350); return () => clearTimeout(t); }, [q]);
  useEffect(() => { api.get("/platform/staff").then((r) => setStaff(r.data.items || [])).catch(() => setStaff([])); }, []);

  const params = useMemo(() => {
    const p = { limit: FETCH_CAP };
    const who = actor || dq;
    if (who) p.q = who;
    if (start) p.start = start;
    if (end) p.end = end;
    return p;
  }, [actor, dq, start, end]);

  const latest = useLatestRequest();
  const load = () => {
    setRefreshing(true); const signal = latest();
    api.get("/platform/audit", { params, signal })
      .then((r) => { setItems(r.data.items); setPage(0); })
      .catch((e) => { if (isCanceled(e)) return; setItems([]); if (e?.response?.status === 400) toast.error("Format tanggal harus YYYY-MM-DD"); })
      .finally(() => { if (!signal.aborted) setRefreshing(false); });
  };
  useEffect(load, [params]); // eslint-disable-line react-hooks/exhaustive-deps

  const download = async () => {
    setDownloading(true);
    try {
      const p = {}; const who = actor || dq; if (who) p.q = who; if (start) p.start = start; if (end) p.end = end;
      const r = await api.get("/platform/audit/export.csv", { params: p, responseType: "blob" });
      const url = URL.createObjectURL(r.data); const a = document.createElement("a");
      a.href = url; a.download = `oryntix-audit-${start || "awal"}-${end || "kini"}.csv`; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error("Gagal mengunduh CSV"); } finally { setDownloading(false); }
  };

  const clearFilters = () => { setQ(""); setDq(""); setActor(""); setStart(""); setEnd(""); setFilter("all"); };
  const hasFilter = q || actor || start || end || filter !== "all";

  const rows = useMemo(() => {
    const list = items || [];
    return filter === "all" ? list : list.filter((a) => (ACTIONS[a.action] || fallback(a.action)).cat === filter);
  }, [items, filter]);
  const pages = Math.ceil(rows.length / PAGE_SIZE);
  const pageRows = rows.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const setF = (id) => { setFilter(id); setPage(0); };

  return (
    <div data-testid="platform-audit-page">
      <PageHeader icon={History} title="Jejak Audit" description="Riwayat aksi staf platform — pemberian/pencabutan peran, penyesuaian kredit, status akun, dan ekspor laporan.">
        <div className="flex flex-wrap gap-2">
          <button onClick={download} disabled={downloading} className="btn-grad inline-flex items-center gap-1.5 rounded-xl px-3.5 py-2 text-xs font-bold disabled:opacity-50" data-testid="audit-download">
            {downloading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} Unduh CSV
          </button>
          <button onClick={load} disabled={refreshing} className="inline-flex items-center gap-1.5 rounded-xl border border-line bg-white px-3 py-2 text-xs font-bold text-slate-600 hover:border-brand disabled:opacity-50" data-testid="audit-refresh">
            <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} /> Segarkan
          </button>
        </div>
      </PageHeader>

      <StaffLogins staff={staff} roleName={roleName} />

      <div className="mt-5 aivora-card flex flex-wrap items-end gap-3 p-4" data-testid="audit-filterbar">
        <label className="w-full flex-1 sm:w-auto sm:min-w-[220px]">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Cari aktor (email)</span>
          <div className="relative mt-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} disabled={!!actor} placeholder={actor ? "Staf dipilih" : "mis. admin@oryntix.com"} data-testid="audit-search" className="w-full rounded-xl border border-line py-2 pl-9 pr-3 text-sm outline-none focus:border-brand disabled:bg-slate-50" />
          </div>
        </label>
        <label>
          <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Staf</span>
          <select value={actor} onChange={(e) => setActor(e.target.value)} data-testid="audit-staff" className="mt-1 block max-w-[240px] rounded-xl border border-line bg-white px-3 py-2 text-sm outline-none focus:border-brand">
            <option value="">Semua staf</option>
            {staff.map((s) => <option key={s.id} value={s.email}>{s.name ? `${s.name} · ` : ""}{s.email} ({roleName(s.platform_role)})</option>)}
          </select>
        </label>
        <label>
          <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Dari</span>
          <input type="date" value={start} max={end || undefined} onChange={(e) => setStart(e.target.value)} data-testid="audit-start" className="mt-1 block rounded-xl border border-line bg-white px-3 py-2 text-sm outline-none focus:border-brand" />
        </label>
        <label>
          <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Sampai</span>
          <input type="date" value={end} min={start || undefined} onChange={(e) => setEnd(e.target.value)} data-testid="audit-end" className="mt-1 block rounded-xl border border-line bg-white px-3 py-2 text-sm outline-none focus:border-brand" />
        </label>
        {hasFilter && (
          <button onClick={clearFilters} className="inline-flex items-center gap-1 rounded-xl bg-slate-100 px-3 py-2 text-xs font-bold text-slate-600 hover:bg-slate-200" data-testid="audit-clear">
            <X size={13} /> Reset
          </button>
        )}
      </div>

      <div className="mt-4 flex flex-wrap gap-2" data-testid="audit-filters">
        {FILTERS.map((f) => (
          <button key={f.id} onClick={() => setF(f.id)} data-testid={`audit-filter-${f.id}`}
            className={`rounded-full px-3.5 py-1.5 text-xs font-bold transition-colors ${filter === f.id ? "bg-brand text-white" : "bg-white text-slate-600 border border-line hover:border-brand"}`}>
            {f.label}
          </button>
        ))}
      </div>

      <div className="mt-4 aivora-card divide-y divide-slate-100 overflow-hidden" data-testid="audit-list">
        {items === null ? (
          <p className="flex items-center gap-2 p-6 text-sm text-slate-400"><Loader2 size={14} className="animate-spin" /> Memuat…</p>
        ) : rows.length === 0 ? (
          <p className="p-6 text-sm text-slate-400" data-testid="audit-empty">Tidak ada aktivitas yang cocok dengan filter.</p>
        ) : pageRows.map((a) => {
          const def = ACTIONS[a.action] || fallback(a.action);
          const Icon = def.icon;
          return (
            <button type="button" key={a.id} onClick={() => setSel(a)} className="flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-slate-50 focus:bg-slate-50 focus:outline-none" data-testid={`audit-row-${a.id}`}>
              <span className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${def.tint}`}><Icon size={16} /></span>
              <div className="min-w-0 flex-1">
                <p className="text-sm text-slate-800"><span className="font-bold">{a.actor_email}</span> · {def.describe(a.target, a.meta)}</p>
                <p className="mt-0.5 flex items-center gap-2 text-[11px] text-slate-400">
                  <span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-slate-500">{def.label}</span>
                  {fmt(a.created_at)}
                  {a.action === "auth.login" && isOddHour(a.created_at) && <span className="rounded bg-rose-50 px-1.5 py-0.5 font-semibold text-rose-700">jam tak wajar</span>}
                  {splitMeta(a.meta).diffs.length > 0 && <span className="rounded bg-brand-soft px-1.5 py-0.5 font-semibold text-brand">ada perubahan</span>}
                </p>
              </div>
              <ChevronRight size={16} className="mt-2 shrink-0 text-slate-300" />
            </button>
          );
        })}
      </div>
      {sel && <AuditDetail entry={sel} def={ACTIONS[sel.action] || fallback(sel.action)} onClose={closeSel} />}

      <Pager page={page} pages={pages} total={rows.length} size={PAGE_SIZE} onPage={(p) => { setPage(p); window.scrollTo({ top: 0, behavior: "smooth" }); }} testid="audit-pager" />
      {items !== null && items.length >= FETCH_CAP && (
        <p className="mt-2 text-center text-[11px] text-slate-400" data-testid="audit-cap-note">Menampilkan {FETCH_CAP} aktivitas terbaru. Persempit rentang tanggal untuk melihat yang lebih lama.</p>
      )}
    </div>
  );
}
